// src/pages/OrgDashboardPage.jsx
//
// GET /api/tracker/dashboard/org/?status=&project_no=&year=&totals_status=
// returns { projects: [...], totals: {...} }. Each project entry only has
// a key for a stage if that stage's row exists (tracker/dashboard.py's
// get_all_rows() only sets entry[group.label] when it finds a row) --
// so a missing key renders as "--", same convention as ProjectListPage.
//
// totals is { stage_label: { count, total } }, count being how many of the
// (filtered) projects have that stage's status equal to totals_status
// (defaults to "approved" server-side). The "Count towards fraction"
// dropdown here is what makes that customizable, per Jerome's earlier
// request -- swapping it re-fetches with a different totals_status.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/client";
import Navbar from "../components/Navbar";
import { ALL_STATUS_CHOICES, STAGE_TITLES, anyStatusLabel } from "../constants";

const STAGE_KEYS = [
  "permit",
  "fieldwork",
  "site_data",
  "final_report",
  "digital_data",
  "artifact_submission",
];

function buildQuery(filters) {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.project_no) params.set("project_no", filters.project_no);
  if (filters.year) params.set("year", filters.year);
  if (filters.totals_status) params.set("totals_status", filters.totals_status);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export default function OrgDashboardPage() {
  const [filters, setFilters] = useState({
    status: "",
    project_no: "",
    year: "",
    totals_status: "approved",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [projects, setProjects] = useState([]);
  const [totals, setTotals] = useState({});
  const [projectIdByNo, setProjectIdByNo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The dashboard endpoint only returns project_no/project_name (see
  // tracker/dashboard.py's get_all_rows) -- no numeric id, so it can't
  // link straight to /projects/:projectId. Fetch the plain project list
  // once, unfiltered, just to build a project_no -> id lookup for links.
  useEffect(() => {
    apiFetch("/tracker/projects/")
      .then((list) => {
        const byNo = {};
        list.forEach((p) => {
          byNo[p.project_no] = p.id;
        });
        setProjectIdByNo(byNo);
      })
      .catch(() => setProjectIdByNo({}));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    apiFetch(`/tracker/dashboard/org/${buildQuery(appliedFilters)}`)
      .then((data) => {
        if (cancelled) return;
        setProjects(data.projects || []);
        setTotals(data.totals || {});
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load the dashboard.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appliedFilters]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    const cleared = { status: "", project_no: "", year: "", totals_status: "approved" };
    setFilters(cleared);
    setAppliedFilters(cleared);
  };

  return (
    <>
      <Navbar />
      <main className="page page-wide">
        <div className="page-header">
          <h1>Org-wide dashboard</h1>
        </div>

        <form onSubmit={handleFilterSubmit} className="dashboard-filters">
          <div>
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="">Any</option>
              {ALL_STATUS_CHOICES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Project #</label>
            <input
              type="text"
              placeholder="e.g. 24-011"
              value={filters.project_no}
              onChange={(e) => setFilters((f) => ({ ...f, project_no: e.target.value }))}
            />
          </div>
          <div>
            <label>Year (2-digit prefix)</label>
            <input
              type="text"
              placeholder="e.g. 24"
              maxLength={2}
              value={filters.year}
              onChange={(e) => setFilters((f) => ({ ...f, year: e.target.value }))}
            />
          </div>
          <div>
            <label>Count towards fraction</label>
            <select
              value={filters.totals_status}
              onChange={(e) => setFilters((f) => ({ ...f, totals_status: e.target.value }))}
            >
              {ALL_STATUS_CHOICES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="dashboard-filters-actions">
            <button type="submit" className="btn-primary">
              Apply
            </button>
            <button type="button" onClick={handleClearFilters}>
              Clear
            </button>
          </div>
        </form>

        {loading && <p>Loading...</p>}
        {error && <p className="form-error">{error}</p>}

        {!loading && !error && (
          <>
            <div className="dashboard-totals-grid">
              {STAGE_KEYS.map((key) => {
                const t = totals[key] || { count: 0, total: 0 };
                return (
                  <div key={key} className="metric-card">
                    <p className="metric-card-label">{STAGE_TITLES[key]}</p>
                    <p className="metric-card-value">
                      {t.count}/{t.total}
                    </p>
                    <p className="metric-card-sub">{anyStatusLabel(appliedFilters.totals_status)}</p>
                  </div>
                );
              })}
            </div>

            <div className="table-wrap table-wrap-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Project #</th>
                    <th>Name</th>
                    {STAGE_KEYS.map((key) => (
                      <th key={key}>{STAGE_TITLES[key]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 && (
                    <tr>
                      <td colSpan={2 + STAGE_KEYS.length} className="empty-row">
                        No projects match these filters.
                      </td>
                    </tr>
                  )}
                  {projects.map((project) => {
                    const projectId = projectIdByNo[project.project_no];
                    return (
                    <tr key={project.project_no}>
                      <td>
                        {projectId ? (
                          <Link to={`/projects/${projectId}`}>{project.project_no}</Link>
                        ) : (
                          project.project_no
                        )}
                      </td>
                      <td>{project.project_name || "--"}</td>
                      {STAGE_KEYS.map((key) => {
                        const value = project[key];
                        return (
                          <td key={key}>
                            <span className={`status-badge status-${value || "none"}`}>
                              {value ? anyStatusLabel(value) : "--"}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </>
  );
}
